import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Tooltip } from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  listDeduplicationJobs,
  deleteDeduplicationJob,
  type DeduplicationJob,
  DeduplicationJobStatuses,
} from '@/api/deduplicationApi';
import { DataManagementModules } from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';
import DataManagementNav from './DataManagementNav';

const statusColors: Record<string, string> = {
  pending: 'default',
  scanning: 'processing',
  review: 'blue',
  merging: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function DeduplicationJobListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [moduleFilter, setModuleFilter] = useState<string | undefined>();

  const canRead = hasPermission('data_management', 'read');
  const canDelete = hasPermission('data_management', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: Record<string, any> = { ...params };
      if (statusFilter) filters.status = statusFilter;
      if (moduleFilter) filters.target_module = moduleFilter;
      return listDeduplicationJobs(filters);
    },
    [statusFilter, moduleFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('data_management.dedup.job.delete_confirm'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteDeduplicationJob(id);
            message.success(t('data_management.dedup.job.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<DeduplicationJob> = useMemo(
    () => [
      {
        title: t('data_management.dedup.job.field.rule_name'),
        dataIndex: 'rule_name',
        key: 'rule_name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('data_management.dedup.job.field.target_module'),
        dataIndex: 'target_module',
        key: 'target_module',
        width: 120,
        render: (v: string) => t(`data_management.import.modules.${v}`, v),
      },
      {
        title: t('data_management.dedup.job.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (v: string) => (
          <Tag color={statusColors[v] ?? 'default'}>
            {t(`data_management.dedup.job.statuses.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('data_management.dedup.job.field.total_scanned'),
        dataIndex: 'total_scanned',
        key: 'total_scanned',
        width: 90,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.job.field.duplicate_groups_found'),
        dataIndex: 'duplicate_groups_found',
        key: 'duplicate_groups_found',
        width: 100,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.job.field.records_merged'),
        dataIndex: 'records_merged',
        key: 'records_merged',
        width: 80,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.job.field.records_skipped'),
        dataIndex: 'records_skipped',
        key: 'records_skipped',
        width: 80,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.job.field.started_at'),
        dataIndex: 'started_at',
        key: 'started_at',
        width: 160,
        sorter: true,
        render: (v: string | null) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 100,
        render: (_: unknown, record: DeduplicationJob) => (
          <Space>
            {canRead && (
              <Tooltip title={t('app.view')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/data-management/dedup-jobs/${record.id}`)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('app.delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canRead, canDelete, navigate, handleDelete]
  );

  return (
    <div>
      <DataManagementNav />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('data_management.dedup.job.field.status')}
          style={{ width: 140 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[...DeduplicationJobStatuses].map((s) => ({
            value: s,
            label: t(`data_management.dedup.job.statuses.${s}`),
          }))}
        />
        <Select
          allowClear
          placeholder={t('data_management.dedup.job.field.target_module')}
          style={{ width: 140 }}
          value={moduleFilter}
          onChange={setModuleFilter}
          options={DataManagementModules.map((m) => ({
            value: m,
            label: t(`data_management.import.modules.${m}`),
          }))}
        />
      </Space>
      <DataGrid
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />
    </div>
  );
}
